import { useEffect, useRef } from "react";
import { X, Activity, Flame, Leaf, TrendingUp, BookOpen } from "lucide-react";
import { SectionLabel } from "@/components/ui/Card";
import type { PmcInterpretation } from "@/types";

export type PmcMetricKey = "ctl" | "atl" | "tsb" | "ramp" | "all";

interface Props {
  onClose: () => void;
  initialMetric?: PmcMetricKey;
  ctl: number;
  atl: number;
  tsb: number;
  phase: string | null;
  weeksToRace: number | null;
  interpretation: PmcInterpretation | null;
}

export function PmcExplainerModal({
  onClose,
  initialMetric = "all",
  ctl,
  atl,
  tsb,
  phase,
  weeksToRace,
  interpretation,
}: Props) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const ctlRef = useRef<HTMLDivElement | null>(null);
  const atlRef = useRef<HTMLDivElement | null>(null);
  const tsbRef = useRef<HTMLDivElement | null>(null);
  const rampRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (initialMetric === "all" || !bodyRef.current) return;
    const target =
      initialMetric === "ctl"
        ? ctlRef.current
        : initialMetric === "atl"
          ? atlRef.current
          : initialMetric === "tsb"
            ? tsbRef.current
            : rampRef.current;
    if (target) {
      target.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [initialMetric]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-cream-border bg-cream-light shadow-soft"
      >
        <div className="flex items-start justify-between gap-3 border-b border-cream-border p-5">
          <div>
            <SectionLabel>PMC — Performance Manager Chart</SectionLabel>
            <h3 className="mt-1 font-serif text-[22px] font-medium leading-tight text-navy-deep">
              Что означают эти цифры
            </h3>
            {phase ? (
              <div className="mt-1 text-[12px] text-taupe">
                Фаза: <span className="font-medium text-navy-deep">{phase}</span>
                {weeksToRace != null ? (
                  <> · до гонки <span className="font-medium text-navy-deep">{weeksToRace}</span> нед.</>
                ) : null}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-taupe hover:text-navy-deep"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div ref={bodyRef} className="space-y-5 overflow-y-auto p-5">
          <IntroBlock />

          <div ref={ctlRef}>
            <CtlBlock
              value={ctl}
              interp={interpretation?.ctl ?? null}
              highlighted={initialMetric === "ctl"}
            />
          </div>

          <div ref={atlRef}>
            <AtlBlock value={atl} highlighted={initialMetric === "atl"} />
          </div>

          <div ref={tsbRef}>
            <TsbBlock
              value={tsb}
              interp={interpretation?.tsb ?? null}
              highlighted={initialMetric === "tsb"}
            />
          </div>

          {interpretation?.ramp_rate ? (
            <div ref={rampRef}>
              <RampRateBlock
                delta={interpretation.ramp_rate.delta_per_week}
                status={interpretation.ramp_rate.status}
                label={interpretation.ramp_rate.status_label}
                detail={interpretation.ramp_rate.detail}
                highlighted={initialMetric === "ramp"}
              />
            </div>
          ) : null}

          <FooterReference />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

function IntroBlock() {
  return (
    <div className="rounded-xl border border-cream-border bg-cream/60 p-4">
      <p className="text-[14px] leading-relaxed text-navy-deep">
        <strong>PMC</strong> (Performance Manager Chart) показывает три числа:
        сколько у тебя накоплено fitness, сколько свежей усталости и какая форма
        прямо сейчас. Ниже — что каждое значит лично для тебя.
      </p>
    </div>
  );
}

function MetricRow({
  icon,
  label,
  value,
  meaning,
  tone,
  highlighted,
  badge,
  detail,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  meaning: string;
  tone: "good" | "caution" | "danger" | "neutral";
  highlighted?: boolean;
  badge?: string | null;
  detail?: string | null;
  children?: React.ReactNode;
}) {
  const toneBorder =
    tone === "danger"
      ? "border-coral/50"
      : tone === "caution"
        ? "border-amber/50"
        : tone === "good"
          ? "border-amber/30"
          : "border-cream-border";
  const badgeCls =
    tone === "danger"
      ? "bg-coral/15 text-coral"
      : tone === "caution"
        ? "bg-amber/15 text-amber-dark"
        : tone === "good"
          ? "bg-amber/10 text-amber-dark"
          : "bg-cream text-taupe";
  return (
    <section
      className={`rounded-xl border p-4 transition-colors ${toneBorder} ${highlighted ? "ring-2 ring-amber/40" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 text-amber-dark">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="section-label">{label}</span>
            <span className="metric-number text-[22px] text-navy-deep">
              {value}
            </span>
            {badge ? (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${badgeCls}`}>
                {badge}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[14px] leading-snug text-navy-deep">{meaning}</p>
          {detail ? (
            <p className="mt-2 text-[13px] leading-relaxed text-taupe">{detail}</p>
          ) : null}
          {children}
        </div>
      </div>
    </section>
  );
}

function CtlBlock({
  value,
  interp,
  highlighted,
}: {
  value: number;
  interp: { band_label: string; detail: string; target_ctl: number | null; gap_to_target: number | null } | null;
  highlighted: boolean;
}) {
  const badge = interp?.band_label ?? null;
  const gapLine =
    interp?.target_ctl != null && interp.gap_to_target != null
      ? `План на эту неделю — CTL ${Math.round(interp.target_ctl)} (разница ${interp.gap_to_target > 0 ? "−" : "+"}${Math.abs(Math.round(interp.gap_to_target))}).`
      : null;
  return (
    <MetricRow
      icon={<Activity className="h-5 w-5" strokeWidth={1.8} />}
      label="CTL · Fitness"
      value={Math.round(value).toString()}
      tone="neutral"
      badge={badge}
      highlighted={highlighted}
      meaning="Хронический объём — твой «fitness». 42-дневная средняя дневного TSS. Растёт медленно, падает медленно."
      detail={interp?.detail ?? "Держи ramp 3–7 пунктов в неделю — так копится forma без травм."}
    >
      {gapLine ? (
        <p className="mt-2 rounded-md bg-cream/80 px-3 py-1.5 text-[12px] text-navy-deep">
          {gapLine}
        </p>
      ) : null}
    </MetricRow>
  );
}

function AtlBlock({ value, highlighted }: { value: number; highlighted: boolean }) {
  return (
    <MetricRow
      icon={<Flame className="h-5 w-5" strokeWidth={1.8} />}
      label="ATL · Усталость"
      value={Math.round(value).toString()}
      tone="neutral"
      highlighted={highlighted}
      meaning="Острая нагрузка — свежая усталость за последние 7 дней. Нужна для роста, но превышение над CTL больше чем на 30 даёт перетренированность."
      detail="ATL сам по себе не интерпретируется — смотри разницу с CTL (TSB ниже)."
    />
  );
}

function TsbBlock({
  value,
  interp,
  highlighted,
}: {
  value: number;
  interp: { zone_key: string; status: string; status_label: string; headline: string; detail: string } | null;
  highlighted: boolean;
}) {
  const zoneKey = interp?.zone_key ?? "neutral";
  const tone = interp?.status === "danger" ? "danger" : interp?.status === "caution" ? "caution" : "good";
  return (
    <MetricRow
      icon={<Leaf className="h-5 w-5" strokeWidth={1.8} />}
      label="TSB · Форма"
      value={`${Math.round(value) > 0 ? "+" : ""}${Math.round(value)}`}
      tone={tone as "good" | "caution" | "danger"}
      badge={interp?.status_label ?? null}
      highlighted={highlighted}
      meaning={interp?.headline ?? "CTL минус ATL — разница между fitness и усталостью. Положительный TSB = свежий, отрицательный = под нагрузкой."}
      detail={interp?.detail ?? "Держи в зоне -10..+5 для продуктивной работы, +10..+25 под старт."}
    >
      <TsbZonesTable activeZone={zoneKey} />
    </MetricRow>
  );
}

function TsbZonesTable({ activeZone }: { activeZone: string }) {
  const zones = [
    { key: "detraining", range: "> +25", label: "Слишком свежий", note: "Риск потерять fitness" },
    { key: "fresh", range: "+5 … +25", label: "Race-ready", note: "Оптимум для гонки" },
    { key: "neutral", range: "−10 … +5", label: "Рабочий", note: "Продуктивная тренировка" },
    { key: "overload", range: "−10 … −30", label: "Под нагрузкой", note: "OK в Build, следи за сном" },
    { key: "danger", range: "< −30", label: "Опасная зона", note: "Форсированная разгрузка" },
  ];
  return (
    <div className="mt-3">
      {/* Mobile — stacked cards (text wraps freely, no horizontal cramping) */}
      <div className="space-y-2 md:hidden">
        {zones.map((z) => {
          const isActive = z.key === activeZone;
          return (
            <div
              key={z.key}
              className={
                isActive
                  ? "rounded-lg border-2 border-amber bg-amber/15 p-3"
                  : "rounded-lg border border-cream-border bg-cream-light p-3"
              }
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="metric-number text-[13px] text-navy-deep">
                  {z.range}
                </span>
                <span
                  className={
                    isActive
                      ? "text-[13px] font-medium text-amber-dark"
                      : "text-[13px] font-medium text-navy-deep"
                  }
                >
                  {z.label}
                </span>
              </div>
              <div className="mt-1 text-[12px] text-taupe">{z.note}</div>
              {isActive ? (
                <div className="mt-1 text-[11px] font-medium text-amber-dark">
                  ← ты сейчас здесь
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Desktop — compact table */}
      <div className="hidden overflow-hidden rounded-md border border-cream-border md:block">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="bg-cream">
            <th className="px-2 py-1.5 text-left font-medium text-taupe">TSB</th>
            <th className="px-2 py-1.5 text-left font-medium text-taupe">Состояние</th>
            <th className="px-2 py-1.5 text-left font-medium text-taupe">Смысл</th>
          </tr>
        </thead>
        <tbody>
          {zones.map((z) => {
            const isActive = z.key === activeZone;
            return (
              <tr
                key={z.key}
                className={
                  isActive
                    ? "bg-amber/20 font-medium text-navy-deep"
                    : "border-t border-cream-border text-navy-deep"
                }
              >
                <td className="metric-number px-2 py-1.5">{z.range}</td>
                <td className="px-2 py-1.5">{z.label}</td>
                <td className="px-2 py-1.5 text-taupe">{z.note}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function RampRateBlock({
  delta,
  status,
  label,
  detail,
  highlighted,
}: {
  delta: number;
  status: string;
  label: string;
  detail: string;
  highlighted: boolean;
}) {
  const tone = status === "danger" ? "danger" : status === "caution" ? "caution" : "good";
  const sign = delta > 0 ? "+" : "";
  return (
    <MetricRow
      icon={<TrendingUp className="h-5 w-5" strokeWidth={1.8} />}
      label="Ramp rate"
      value={`${sign}${delta.toFixed(1)}/нед`}
      tone={tone as "good" | "caution" | "danger"}
      badge={label}
      highlighted={highlighted}
      meaning="Насколько быстро растёт твой CTL. 3–5 — оптимальный прирост; выше 8 резко повышается риск травм."
      detail={detail}
    />
  );
}

function FooterReference() {
  return (
    <div className="rounded-xl border border-cream-border bg-cream/60 p-4">
      <div className="mb-2 flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-amber-dark" strokeWidth={1.8} />
        <span className="section-label">Модель</span>
      </div>
      <p className="text-[12px] leading-relaxed text-taupe">
        Formula — Coggan Performance Manager Chart (адаптация Banister TRIMP).
        CTL = 42-дневная экспоненциальная средняя дневного TSS. ATL = 7-дневная.
        TSB = CTL − ATL. Это эмпирическая модель — верь числам, но слушай тело.
      </p>
    </div>
  );
}
